import { useState, useRef, useEffect } from 'react';
import { UserPlus, Users, Baby, Calendar, X, MessageSquare, Mail, Send } from 'lucide-react';

export default function InvitationForm({ onClose, onSubmit, existingMember = null }) {
  const nameInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: existingMember?.name || '',
    contact: '',
    dateOfBirth: existingMember?.dateOfBirth || '',
    role: existingMember?.role || 'child'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [contactType, setContactType] = useState(null); // 'email' or 'phone'
  
  // Focus name input when modal opens (only if not pre-filled)
  useEffect(() => {
    if (nameInputRef.current && !existingMember) {
      setTimeout(() => nameInputRef.current?.focus(), 100);
    }
  }, [existingMember]);

  // Auto-save functionality
  useEffect(() => {
    if (!existingMember) {
      const timeoutId = setTimeout(() => {
        if (formData.name.trim() || formData.contact.trim()) {
          localStorage.setItem('invitationDraft', JSON.stringify(formData));
        }
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [formData, existingMember]);

  // Load draft on mount if no existing member
  useEffect(() => {
    if (!existingMember) {
      const draft = localStorage.getItem('invitationDraft');
      if (draft) {
        try {
          const parsedDraft = JSON.parse(draft);
          setFormData(parsedDraft);
        } catch {
          // Ignore parse errors
        }
      }
    }
  }, [existingMember]);

  // Detect contact type (email vs phone)
  useEffect(() => {
    const contact = formData.contact.trim();
    if (!contact) {
      setContactType(null);
      return;
    }

    // Basic email pattern
    if (contact.includes('@')) {
      setContactType('email');
    } 
    // Basic phone pattern (digits, spaces, dashes, parentheses)
    else if (/^[\d\s\-()]+$/.test(contact) && contact.replace(/\D/g, '').length >= 7) {
      setContactType('phone');
    } else {
      setContactType(null);
    }
  }, [formData.contact]);

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!formData.name.trim()) {
        alert('Please enter a name');
        return;
      }

      if (formData.role === 'child' && !formData.dateOfBirth) {
        alert('Please enter date of birth for children');
        return;
      }

      // Prepare submission data
      const submitData = {
        name: formData.name.trim(),
        role: formData.role,
        dateOfBirth: formData.role === 'child' ? formData.dateOfBirth : null,
        email: contactType === 'email' ? formData.contact.trim() : '',
        phone: contactType === 'phone' ? formData.contact.trim() : ''
      };

      if (existingMember) {
        submitData.existingMemberId = existingMember.id;
      }

      await onSubmit(submitData);
      clearDraft();
      onClose();
    } catch (err) {
      console.error('Error creating invitation:', err);
      alert('Error creating invitation: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Move focus to contact field or submit if existing member
      if (!existingMember) {
        document.getElementById('contact-input')?.focus();
      }
    }
  };

  const clearDraft = () => {
    localStorage.removeItem('invitationDraft');
  };

  const age = calculateAge(formData.dateOfBirth);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-neutral-50 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-neutral-50 z-10">
          <h2 className="text-xl font-bold text-stone-900">
            {existingMember ? `Invite ${existingMember.name}` : 'Invite Family Member'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-stone-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name Input - Only show if not existing member */}
          {!existingMember && (
            <div>
              <p className="text-sm text-stone-600 mb-3">Who are you inviting?</p>
              <input
                ref={nameInputRef}
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                onKeyPress={handleNameKeyPress}
                onFocus={() => setNameFocused(true)}
                onBlur={() => setNameFocused(false)}
                className={`w-full px-6 py-4 text-lg border-2 rounded-xl transition-all duration-200 ${
                  nameFocused || formData.name 
                    ? 'border-azure-500 bg-teal-50/50' 
                    : 'border-stone-200 hover:border-stone-300'
                } focus:outline-none focus:ring-0`}
                placeholder="Their name..."
                autoComplete="off"
                required
              />
            </div>
          )}

          {/* Visual Role Selection */}
          <div>
            <p className="text-sm font-medium text-stone-700 mb-3">They are a...</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, role: 'parent' }))}
                className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                  formData.role === 'parent'
                    ? 'border-azure-500 bg-teal-50 shadow-md transform scale-[1.02]'
                    : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                }`}
              >
                <Users className={`w-8 h-8 mx-auto mb-2 ${
                  formData.role === 'parent' ? 'text-teal-600' : 'text-stone-400'
                }`} />
                <span className={`block text-sm font-medium ${
                  formData.role === 'parent' ? 'text-teal-700' : 'text-stone-700'
                }`}>
                  Parent
                </span>
              </button>
              
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, role: 'child' }))}
                className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                  formData.role === 'child'
                    ? 'border-azure-500 bg-teal-50 shadow-md transform scale-[1.02]'
                    : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                }`}
              >
                <Baby className={`w-8 h-8 mx-auto mb-2 ${
                  formData.role === 'child' ? 'text-teal-600' : 'text-stone-400'
                }`} />
                <span className={`block text-sm font-medium ${
                  formData.role === 'child' ? 'text-teal-700' : 'text-stone-700'
                }`}>
                  Child
                </span>
              </button>
            </div>
          </div>

          {/* Date of Birth for Children */}
          {formData.role === 'child' && (
            <div className={`transition-all duration-300 ${showDatePicker ? 'opacity-100' : 'opacity-90'}`}>
              <p className="text-sm font-medium text-stone-700 mb-3">When were they born?</p>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => setFormData(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                  onFocus={() => setShowDatePicker(true)}
                  onBlur={() => setShowDatePicker(false)}
                  className="w-full pl-12 pr-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  required={formData.role === 'child'}
                />
                {age !== null && (
                  <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-sm text-stone-500">
                    Age {age}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Smart Contact Input */}
          <div>
            <p className="text-sm font-medium text-stone-700 mb-3">How should we reach them? (optional)</p>
            <div className="relative">
              {contactType && (
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  {contactType === 'email' ? (
                    <Mail className="w-5 h-5 text-teal-500" />
                  ) : (
                    <MessageSquare className="w-5 h-5 text-indigo-700" />
                  )}
                </div>
              )}
              <input
                id="contact-input"
                type="text"
                value={formData.contact}
                onChange={(e) => setFormData(prev => ({ ...prev, contact: e.target.value }))}
                className={`w-full ${contactType ? 'pl-12' : 'px-4'} pr-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-azure-500 focus:border-azure-500 transition-all duration-200`}
                placeholder="Email or phone number..."
                autoComplete="off"
              />
              {contactType && (
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-xs text-stone-500">
                  {contactType === 'email' ? 'Email invitation' : 'SMS invitation'}
                </span>
              )}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-teal-50 border border-azure-200 rounded-xl p-4">
            <p className="text-sm text-teal-700">
              {existingMember ? (
                <>An invitation code will be generated for {existingMember.name} to create their account and join the family.</>
              ) : (
                <>We'll create an invitation code that {formData.name || 'they'} can use to join your family. 
                {formData.contact && ` We'll also send the code to their ${contactType}.`}</>
              )}
            </p>
          </div>

          {/* Auto-save indicator */}
          {!existingMember && (formData.name.trim() || formData.contact.trim()) && (
            <div className="flex items-center justify-center py-2">
              <div className="flex items-center text-xs text-stone-500">
                <div className="w-2 h-2 bg-indigo-600 rounded-full mr-2 animate-pulse"></div>
                Auto-saving...
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting || !formData.name.trim() || (formData.role === 'child' && !formData.dateOfBirth)}
              className="w-full inline-flex items-center justify-center px-8 py-4 bg-teal-600 text-neutral-50 font-semibold text-lg rounded-xl hover:bg-teal-700 transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-neutral-50 mr-3"></div>
                  Creating Invitation...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Send Invitation
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}